import type { HistoryFlush } from "./service.ts";
import { ArrowsClockwiseIcon, CaretDownIcon, CaretRightIcon, ClockCounterClockwise, FileArrowUp, GitCommit, Plus, Tag, Trash } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { ResourceTreeAction, ResourceTreeRow } from "../../ResourceExplorerTree.tsx";
import type { HistoryTag as TagRecord, HistoryRevision as RevisionRecord } from "../../api/history/model.ts";
import { historySelectionKey, shortHistoryDate as shortDate } from "./model.ts";
import { useHistory } from "./react.tsx";

interface HistoryExplorerProps {
  stationId: string | null;
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
  onFlushPending: HistoryFlush;
}

export function HistoryExplorer({ stationId, workspaceId, connectionName, collapsed, headerLeading, refreshToken, onCollapsedChange, onFlushPending }: HistoryExplorerProps) {
  const [tagsExpanded, setTagsExpanded] = useState(true), [historyExpanded, setHistoryExpanded] = useState(true);
  const [expandedRevisions, setExpandedRevisions] = useState<Set<number>>(new Set());
  const scope = historySelectionKey({ stationId, workspaceId });
  useEffect(() => { setExpandedRevisions(new Set()); }, [scope]);
  const view = useHistory({ stationId, workspaceId, collapsed, ...(refreshToken === undefined ? {} : { refreshToken }) }, onFlushPending);
  const tags = view.snapshot.data?.tags ?? [], revisions = view.snapshot.data?.revisions ?? [];
  const loading = view.snapshot.loading, mutating = view.snapshot.mutating, error = view.error;
  const identity = { selectionKey: view.snapshot.selectionKey, selectionEpoch: view.snapshot.selectionEpoch };
  const refresh = () => view.dispatch({ _tag: "Refresh" });
  const createTag = () => { if (view.ready) view.dispatch({ _tag: "CreateTag", ...identity }); };
  const deleteTag = (tag: TagRecord) => { if (view.ready) view.dispatch({ _tag: "DeleteTag", ...identity, name: tag.name }); };
  const restoreTag = (tag: TagRecord) => { if (view.ready) view.dispatch({ _tag: "RestoreTag", ...identity, name: tag.name }); };
  const restoreRevision = (revision: RevisionRecord, path?: string) => { if (view.ready) view.dispatch({ _tag: "RestoreRevision", ...identity, revision: revision.id, ...(path === undefined ? {} : { path }) }); };
  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            History
          </button>
        )}
        <ResourceTreeAction
          label="Tag complete Workerstation"
          onClick={() => void createTag()}
          disabled={!stationId || loading || mutating || !view.ready}
        >
          <Plus size={13} weight="bold" />
        </ResourceTreeAction>
        <ResourceTreeAction
          label="Refresh history"
          onClick={() => void refresh()}
          disabled={!stationId || loading || mutating || !view.ready}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {stationId && workspaceId ? (
            <>
              <ResourceTreeRow
                depth={0}
                label={connectionName || "Workerstation"}
                detail={`${tags.length} tag${tags.length === 1 ? "" : "s"}`}
                icon={<ClockCounterClockwise size={14} />}
              />
              <ResourceTreeRow
                depth={1}
                label="Workerstation tags"
                detail={`${tags.length}`}
                icon={<Tag size={14} />}
                expandable
                expanded={tagsExpanded}
                onClick={() => setTagsExpanded((value) => !value)}
              />
              {tagsExpanded
                ? tags.map((tag) => (
                    <ResourceTreeRow
                      key={tag.id}
                      depth={2}
                      label={tag.name}
                      detail={shortDate(tag.createdAt)}
                      icon={<GitCommit size={14} />}
                      title={tag.source === "legacy" ? `${tag.message ?? "No message"}\n${tag.workspaceCount} workerspaces · ${tag.fileCount} files` : `${tag.commitId}\nRepository snapshot · revision ${tag.revision}`}
                      actions={
                        <>
                          <ResourceTreeAction
                            label={`Restore tag ${tag.name}`}
                            disabled={mutating}
                            onClick={() => void restoreTag(tag)}
                          >
                            <FileArrowUp size={12} />
                          </ResourceTreeAction>
                          <ResourceTreeAction
                            label={`Delete tag ${tag.name}`}
                            disabled={mutating}
                            onClick={() => void deleteTag(tag)}
                          >
                            <Trash size={12} />
                          </ResourceTreeAction>
                        </>
                      }
                    />
                  ))
                : null}
              <ResourceTreeRow
                depth={1}
                label="Workerspace history"
                detail={`${revisions.length}`}
                icon={<ClockCounterClockwise size={14} />}
                expandable
                expanded={historyExpanded}
                onClick={() => setHistoryExpanded((value) => !value)}
              />
              {historyExpanded
                ? revisions.map((revision) => {
                    const expanded = expandedRevisions.has(revision.id);
                    return (
                      <div key={revision.id}>
                        <ResourceTreeRow
                          depth={2}
                          label={`Revision ${revision.id}`}
                          detail={shortDate(revision.createdAt)}
                          icon={<GitCommit size={14} />}
                          expandable={revision.changedPaths.length > 0}
                          expanded={expanded}
                          onClick={() =>
                            setExpandedRevisions((current) => {
                              const next = new Set(current);
                              if (next.has(revision.id)) next.delete(revision.id);
                              else next.add(revision.id);
                              return next;
                            })
                          }
                          title={`${revision.summary}\n${revision.source}`}
                          actions={
                            <ResourceTreeAction
                              label={`Restore complete revision ${revision.id}`}
                              disabled={mutating}
                              onClick={() => void restoreRevision(revision)}
                            >
                              <FileArrowUp size={12} />
                            </ResourceTreeAction>
                          }
                        />
                        {expanded
                          ? revision.changedPaths.map((path) => (
                              <ResourceTreeRow
                                key={path}
                                depth={3}
                                label={path}
                                icon={<FileArrowUp size={12} />}
                                actions={
                                  <ResourceTreeAction
                                    label={`Restore ${path} from revision ${revision.id}`}
                                    disabled={mutating}
                                    onClick={() =>
                                      void restoreRevision(revision, path)
                                    }
                                  >
                                    <FileArrowUp size={12} />
                                  </ResourceTreeAction>
                                }
                              />
                            ))
                          : null}
                      </div>
                    );
                  })
                : null}
              {!loading && tags.length === 0 && revisions.length === 0 ? (
                <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                  Edit files to create recoverable revisions, or tag the complete
                  Workerstation with the + button.
                </div>
              ) : null}
            </>
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to inspect version history.
            </p>
          )}
          {error ? (
            <p className="px-2 py-1 text-[11px] text-status-error">{error}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
